import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { iconCloseModalCourses } from '../../assets';
import { useTheme } from '../../theme';
import Button from '../Button';
import type {
  DynamicModalActionConfig,
  DynamicModalAutocompleteOption,
  DynamicModalFieldConfig,
  DynamicModalFormValues,
} from './types';

const AUTOCOMPLETE_DROPDOWN_MAX_HEIGHT = 200;
/** Separador do valor armazenado em autocomplete multiselect (ex.: "val1,val2") */
const AUTOCOMPLETE_MULTI_SEPARATOR = ',';

/** Valida se o valor tem formato de e-mail (ex.: local@dominio.com ou usuario@empresa.com.br) */
function isValidEmail(value: string): boolean {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '') return false;
  // Exige: algo antes do @, @, e domínio com pelo menos um ponto (ex: .com, .com.br)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
}

export interface DynamicModalProps {
  /** Título exibido no topo do modal */
  title: string;
  /** Mensagem opcional exibida abaixo do título (ex.: confirmação de exclusão) */
  description?: string;
  /** Configuração dos campos do formulário */
  fields: DynamicModalFieldConfig[];
  /** Botões de ação no rodapé (ex.: Cancel, Create Group) */
  actions: DynamicModalActionConfig[];
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback ao fechar (overlay ou botão X) */
  onClose: () => void;
  /** Se false, clicar no overlay não fecha o modal (apenas ESC e botões). */
  closeOnOverlayClick?: boolean;
  /** Valores iniciais do formulário (opcional) */
  initialValues?: Partial<DynamicModalFormValues>;
  /** Prefixo para identificadores de teste (ex.: "dynamic-modal-user") */
  dataE2ePrefix?: string;
  /** Se true, reduz margens e padding (ex.: modal de confirmação sem campos) */
  compact?: boolean;
}

export default function DynamicModal({
  title,
  description,
  fields,
  actions,
  open,
  onClose,
  closeOnOverlayClick = true,
  initialValues = {},
  dataE2ePrefix,
  compact = false,
}: DynamicModalProps) {
  const theme = useTheme();
  const baseId = useId();

  const normalizeFieldValue = useCallback(
    (
      field: DynamicModalFieldConfig,
      raw: string | boolean | undefined
    ): string => {
      if (field.type === 'switch') {
        if (raw === true || raw === 'true') return 'true';
        return 'false';
      }
      return raw != null ? String(raw) : '';
    },
    []
  );

  const [values, setValues] = useState<DynamicModalFormValues>(() =>
    fields.reduce<DynamicModalFormValues>((acc, f) => {
      acc[f.name] = normalizeFieldValue(f, initialValues[f.name]);
      return acc;
    }, {})
  );

  const [showValidation, setShowValidation] = useState(false);
  const [openAutocomplete, setOpenAutocomplete] = useState<string | null>(null);
  const [autocompleteHighlightedIndex, setAutocompleteHighlightedIndex] =
    useState<number>(-1);
  /** Query digitada no autocomplete multiselect (por nome do campo) */
  const [autocompleteQuery, setAutocompleteQuery] = useState<
    Record<string, string>
  >({});
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);

  type AutocompleteFieldState = {
    options: DynamicModalAutocompleteOption[];
    page: number;
    hasMore: boolean;
    loading: boolean;
    lastSearch: string;
  };

  const [autocompleteState, setAutocompleteState] = useState<
    Record<string, AutocompleteFieldState>
  >({});

  const updateValue = useCallback((name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const isRequired = useCallback((field: DynamicModalFieldConfig) => {
    return field.optional !== true;
  }, []);

  const getRequiredErrors = useCallback(() => {
    const errors: string[] = [];
    fields.forEach(f => {
      if (isRequired(f)) {
        const value = values[f.name];
        const isEmpty =
          (f.type === 'autocomplete' && f.multiselect) ||
          f.type === 'checkboxgroup'
            ? !value ||
              (typeof value === 'string' &&
                value.split(AUTOCOMPLETE_MULTI_SEPARATOR).filter(Boolean)
                  .length === 0)
            : value == null ||
              (typeof value === 'string' && value.trim() === '');
        if (isEmpty) errors.push(f.name);
      }
    });
    return errors;
  }, [fields, values, isRequired]);

  /** Campos do tipo email com valor preenchido mas formato inválido */
  const getEmailFormatErrors = useCallback(() => {
    const errors: string[] = [];
    fields.forEach(f => {
      if (f.type === 'email') {
        const value = values[f.name];
        const trimmed = typeof value === 'string' ? value.trim() : '';
        if (trimmed !== '' && !isValidEmail(trimmed)) errors.push(f.name);
      }
    });
    return errors;
  }, [fields, values]);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      const next: DynamicModalFormValues = {};
      fields.forEach(f => {
        next[f.name] = normalizeFieldValue(f, initialValues[f.name]);
      });
      setValues(next);
      setShowValidation(false);
      setOpenAutocomplete(null);
      setAutocompleteQuery({});
    }
  }, [open, initialValues, fields, normalizeFieldValue]);

  useEffect(() => {
    if (openAutocomplete === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        autocompleteContainerRef.current &&
        !autocompleteContainerRef.current.contains(e.target as Node)
      ) {
        setOpenAutocomplete(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openAutocomplete]);

  const getAutocompleteFieldState = useCallback(
    (fieldName: string): AutocompleteFieldState => {
      return (
        autocompleteState[fieldName] ?? {
          options: [],
          page: 0,
          hasMore: false,
          loading: false,
          lastSearch: '',
        }
      );
    },
    [autocompleteState]
  );

  const setAutocompleteFieldState = useCallback(
    (fieldName: string, next: Partial<AutocompleteFieldState>) => {
      setAutocompleteState(prev => {
        const current = prev[fieldName] ?? {
          options: [],
          page: 0,
          hasMore: false,
          loading: false,
          lastSearch: '',
        };
        return {
          ...prev,
          [fieldName]: { ...current, ...next },
        };
      });
    },
    []
  );

  const loadAutocompleteOptionsPage = useCallback(
    async (
      field: DynamicModalFieldConfig,
      params: { search: string; page: number; append: boolean }
    ) => {
      if (!field.loadOptions) return;
      const pageSize = field.pageSize ?? 50;
      const { search, page, append } = params;

      const key = field.name;
      const current = getAutocompleteFieldState(key);
      if (current.loading) return;

      setAutocompleteFieldState(key, {
        loading: true,
        lastSearch: search,
      });

      try {
        const result = await field.loadOptions({ search, page, pageSize });
        const nextOptions = Array.isArray(result.options)
          ? result.options
          : [];

        setAutocompleteFieldState(key, {
          options: append
            ? (() => {
                const map = new Map(
                  current.options.map(opt => [opt.value, opt] as const)
                );
                nextOptions.forEach(opt => {
                  map.set(opt.value, opt);
                });
                return Array.from(map.values());
              })()
            : nextOptions,
          page,
          hasMore: !!result.hasMore,
        });
      } finally {
        setAutocompleteFieldState(key, {
          loading: false,
        });
      }
    },
    [getAutocompleteFieldState, setAutocompleteFieldState]
  );

  const handleAction = useCallback(
    (action: DynamicModalActionConfig) => () => {
      const isSubmit = action.variant === 'primary';
      if (isSubmit) {
        const requiredErrors = getRequiredErrors();
        const emailFormatErrors = getEmailFormatErrors();
        if (requiredErrors.length > 0 || emailFormatErrors.length > 0) {
          setShowValidation(true);
          return;
        }
        setShowValidation(false);
      }
      action.onClick(values);
    },
    [values, getRequiredErrors, getEmailFormatErrors]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const overlayStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: theme.spacing.lg,
    }),
    [theme.spacing.lg]
  );

  const modalStyle: React.CSSProperties = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius.lg,
      boxShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflow: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    }),
    [theme.palette.background.paper, theme.shape.borderRadius.lg]
  );

  const headerStyle: React.CSSProperties = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${theme.spacing.lg} ${theme.spacing.lg} 0`,
      marginBottom: compact ? theme.spacing.md : theme.spacing.lg,
    }),
    [theme.spacing.lg, theme.spacing.md, compact]
  );

  const titleStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.palette.text.primary,
      margin: 0,
    }),
    [theme.typography, theme.palette.text.primary]
  );

  const closeButtonStyle: React.CSSProperties = useMemo(
    () => ({
      background: 'none',
      border: 'none',
      padding: theme.spacing.xs,
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.shape.borderRadius.sm,
      lineHeight: 1,
      fontSize: '1.25rem',
    }),
    [
      theme.spacing.xs,
      theme.palette.text.secondary,
      theme.shape.borderRadius.sm,
    ]
  );

  const bodyStyle: React.CSSProperties = useMemo(
    () => ({
      padding: `0 ${theme.spacing.lg} ${compact ? theme.spacing.md : theme.spacing.lg}`,
    }),
    [theme.spacing.lg, theme.spacing.md, compact]
  );

  const descriptionStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.base,
      color: theme.palette.text.secondary ?? theme.palette.text.primary,
      lineHeight: theme.typography.lineHeight?.normal ?? 1.5,
      marginBottom: compact ? theme.spacing.sm : theme.spacing.lg,
      marginTop: 0,
    }),
    [theme, compact]
  );

  const textareaStyle: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.input.text,
      backgroundColor: theme.palette.input.background,
      border: `${theme.shape.borderWidth.thin} solid ${theme.palette.input.background}`,
      borderRadius: theme.shape.borderRadius.md,
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      minHeight: '80px',
    }),
    [theme]
  );

  const inputBaseStyle: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.input.text,
      backgroundColor: theme.palette.input.background,
      border: `${theme.shape.borderWidth.thin} solid ${theme.palette.input.background}`,
      borderRadius: theme.shape.borderRadius.md,
      outline: 'none',
      boxSizing: 'border-box',
    }),
    [theme]
  );

  const switchTrackStyle: React.CSSProperties = useMemo(
    () => ({
      width: 44,
      height: 24,
      borderRadius: 12,
      backgroundColor: theme.palette.border.input,
      position: 'relative' as const,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      flexShrink: 0,
    }),
    [theme]
  );

  const switchThumbStyle: React.CSSProperties = useMemo(
    () => ({
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: theme.palette.background.paper,
      position: 'absolute' as const,
      top: 2,
      left: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
    }),
    [theme]
  );

  const labelStyle: React.CSSProperties = useMemo(
    () => ({
      display: 'block',
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing.sm,
    }),
    [theme]
  );

  const errorStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.sm,
      color: theme.palette.error?.main ?? theme.palette.modalDynamic.error,
      marginTop: theme.spacing.xs,
    }),
    [theme]
  );

  const chipContainerStyle = useMemo(
    () =>
      ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing.xs,
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        borderRadius: theme.shape.borderRadius.md,
        fontSize: theme.typography.fontSize.sm,
        fontFamily: theme.typography.fontFamily,
        backgroundColor:
          (
            theme.palette as {
              modalDynamic?: { autocomplete?: { background: string } };
            }
          ).modalDynamic?.autocomplete?.background ??
          theme.palette.modalDynamic.autocomplete.background,
        border: `1px solid ${(theme.palette as { modalDynamic?: { autocomplete?: { border: string } } }).modalDynamic?.autocomplete?.border ?? theme.palette.modalDynamic.autocomplete.border}`,
        color:
          (
            theme.palette as {
              modalDynamic?: { autocomplete?: { text: string } };
            }
          ).modalDynamic?.autocomplete?.text ??
          theme.palette.modalDynamic.autocomplete.text,
      }) as React.CSSProperties,
    [theme]
  );

  const chipRemoveButtonStyle = useMemo(
    () =>
      ({
        marginLeft: theme.spacing.xs,
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: theme.typography.fontSize.base,
        lineHeight: 1,
        color: 'inherit',
        opacity: 0.8,
      }) as React.CSSProperties,
    [theme]
  );

  const footerStyle: React.CSSProperties = useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing.sm,
      padding: compact ? theme.spacing.md : theme.spacing.lg,
      borderTop: `1px solid ${theme.palette.background.paper}`,
    }),
    [theme.spacing.lg, theme.spacing.md, theme.spacing.sm, theme.palette.background.paper, compact]
  );

  if (!open) return null;

  const overlayTestId = dataE2ePrefix
    ? `${dataE2ePrefix}-overlay`
    : undefined;
  const modalTestId = dataE2ePrefix ? `${dataE2ePrefix}-container` : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${baseId}-title`}
      style={overlayStyle}
      onClick={e => {
        if (!closeOnOverlayClick) return;
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
      data-e2e={overlayTestId}
    >
      <style>{`.dynamic-modal-hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
      <div
        className="dynamic-modal-hide-scrollbar"
        style={modalStyle}
        onClick={e => e.stopPropagation()}
        data-e2e={modalTestId}
      >
        <div style={headerStyle}>
          <h2 id={`${baseId}-title`} style={titleStyle}>
            {title}
          </h2>
          <button
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label="Fechar"
          >
            <img
              src={iconCloseModalCourses}
              alt=""
              width={20}
              height={20}
              style={{ objectFit: 'contain' }}
              aria-hidden
            />
          </button>
        </div>

        <div style={bodyStyle}>
          {description ? (
            <p style={descriptionStyle} role="status">
              {description}
            </p>
          ) : null}
          {fields.map(field => {
            const required = isRequired(field);
            const label = required
              ? `${field.label}`
              : field.optional
                ? `${field.label} (Optional)`
                : field.label;
            const value = values[field.name] ?? '';
            const isEmpty =
              field.type === 'checkboxgroup'
                ? !value ||
                  (typeof value === 'string' &&
                    value.split(AUTOCOMPLETE_MULTI_SEPARATOR).filter(Boolean)
                      .length === 0)
                : value == null ||
                  (typeof value === 'string' && value.trim() === '');
            const isInvalidEmail =
              field.type === 'email' && !isEmpty && !isValidEmail(String(value));
            const hasError =
              showValidation &&
              ((required && isEmpty) || (field.type === 'email' && isInvalidEmail));
            const borderColor = hasError
              ? (theme.palette.error?.main ?? theme.palette.modalDynamic.error)
              : theme.palette.border.input;

            if (field.type === 'textarea') {
              return (
                <div
                  key={field.name}
                  style={{ marginBottom: theme.spacing.md }}
                >
                  <label style={labelStyle} htmlFor={`${baseId}-${field.name}`}>
                    {label}
                  </label>
                  <textarea
                    id={`${baseId}-${field.name}`}
                    value={value}
                    onChange={e => updateValue(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    style={{
                      ...textareaStyle,
                      border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                    }}
                    rows={3}
                  />
                  {hasError && (
                    <div style={errorStyle}>Este campo é obrigatório</div>
                  )}
                </div>
              );
            }

            if (field.type === 'autocomplete') {
              const fieldState = getAutocompleteFieldState(field.name);
              const baseOptions =
                field.loadOptions && fieldState.options.length > 0
                  ? fieldState.options
                  : field.options ?? [];
              const options = baseOptions;
              const isMultiselect = field.multiselect === true;

              if (isMultiselect) {
                const selectedValues = (value || '')
                  .split(AUTOCOMPLETE_MULTI_SEPARATOR)
                  .map(s => s.trim())
                  .filter(Boolean);
                const query = autocompleteQuery[field.name] ?? '';
                const labelOptionsForChips =
                  field.loadOptions && field.options
                    ? [...field.options, ...options]
                    : options;
                const filteredOptions =
                  field.loadOptions && query.trim()
                    ? options.filter(opt => !selectedValues.includes(opt.value))
                    : options.filter(opt => !selectedValues.includes(opt.value));
                const isOpen = openAutocomplete === field.name;
                const showDropdown = isOpen && filteredOptions.length > 0;
                const highlightedIndex =
                  autocompleteHighlightedIndex < 0 ||
                  autocompleteHighlightedIndex >= filteredOptions.length
                    ? -1
                    : autocompleteHighlightedIndex;

                const multiselectInputContainerStyle: React.CSSProperties = {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  padding: theme.spacing.sm,
                  minHeight: 42,
                  fontSize: theme.typography.fontSize.base,
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.input.text,
                  backgroundColor: theme.palette.input.background,
                  border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                  borderRadius: theme.shape.borderRadius.md,
                  boxSizing: 'border-box',
                };

                return (
                  <div
                    key={field.name}
                    ref={
                      openAutocomplete === field.name
                        ? autocompleteContainerRef
                        : undefined
                    }
                    style={{
                      position: 'relative',
                      marginBottom: theme.spacing.md,
                    }}
                  >
                    <label
                      style={labelStyle}
                      htmlFor={`${baseId}-${field.name}-input`}
                    >
                      {label}
                    </label>
                    <div
                      style={multiselectInputContainerStyle}
                      onClick={() =>
                        document
                          .getElementById(`${baseId}-${field.name}-input`)
                          ?.focus()
                      }
                    >
                      {selectedValues.map(val => {
                        const label =
                          labelOptionsForChips.find(o => o.value === val)
                            ?.label ?? val;
                        return (
                          <span
                            key={val}
                            style={chipContainerStyle}
                            title={label}
                          >
                            {label}
                            <button
                              type="button"
                              aria-label={`Remover ${label}`}
                              style={chipRemoveButtonStyle}
                              onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                const next = selectedValues.filter(
                                  v => v !== val
                                );
                                updateValue(
                                  field.name,
                                  next.join(AUTOCOMPLETE_MULTI_SEPARATOR)
                                );
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      <input
                        id={`${baseId}-${field.name}-input`}
                        role="combobox"
                        aria-expanded={showDropdown}
                        aria-autocomplete="list"
                        aria-controls={
                          showDropdown
                            ? `${baseId}-${field.name}-listbox`
                            : undefined
                        }
                        aria-activedescendant={
                          highlightedIndex >= 0 &&
                          filteredOptions[highlightedIndex]
                            ? `${baseId}-${field.name}-opt-${filteredOptions[highlightedIndex].value}`
                            : undefined
                        }
                        value={query}
                        onChange={e => {
                          const nextQuery = e.target.value;
                          setAutocompleteQuery(prev => ({
                            ...prev,
                            [field.name]: nextQuery,
                          }));
                          setOpenAutocomplete(field.name);
                          setAutocompleteHighlightedIndex(0);
                          if (field.loadOptions) {
                            loadAutocompleteOptionsPage(field, {
                              search: nextQuery.trim(),
                              page: 1,
                              append: false,
                            });
                          }
                        }}
                        onFocus={() => {
                          setOpenAutocomplete(field.name);
                          setAutocompleteHighlightedIndex(0);
                          if (field.loadOptions) {
                            loadAutocompleteOptionsPage(field, {
                              search: (autocompleteQuery[field.name] ?? '').trim(),
                              page: 1,
                              append: false,
                            });
                          }
                        }}
                        onKeyDown={e => {
                          if (!showDropdown) {
                            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                              setOpenAutocomplete(field.name);
                              setAutocompleteHighlightedIndex(0);
                              if (field.loadOptions) {
                                loadAutocompleteOptionsPage(field, {
                                  search: query.trim(),
                                  page: 1,
                                  append: false,
                                });
                              }
                            }
                            if (
                              e.key === 'Backspace' &&
                              !query &&
                              selectedValues.length > 0
                            ) {
                              const next = selectedValues.slice(0, -1);
                              updateValue(
                                field.name,
                                next.join(AUTOCOMPLETE_MULTI_SEPARATOR)
                              );
                            }
                            return;
                          }
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setAutocompleteHighlightedIndex(prev =>
                              prev < filteredOptions.length - 1 ? prev + 1 : 0
                            );
                            return;
                          }
                          if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setAutocompleteHighlightedIndex(prev =>
                              prev <= 0 ? filteredOptions.length - 1 : prev - 1
                            );
                            return;
                          }
                          if (
                            e.key === 'Enter' &&
                            highlightedIndex >= 0 &&
                            filteredOptions[highlightedIndex]
                          ) {
                            e.preventDefault();
                            const opt = filteredOptions[highlightedIndex];
                            const next = [...selectedValues, opt.value];
                            updateValue(
                              field.name,
                              next.join(AUTOCOMPLETE_MULTI_SEPARATOR)
                            );
                            setAutocompleteQuery(prev => ({
                              ...prev,
                              [field.name]: '',
                            }));
                            setAutocompleteHighlightedIndex(0);
                          }
                          if (e.key === 'Escape') {
                            setOpenAutocomplete(null);
                            setAutocompleteHighlightedIndex(-1);
                          }
                        }}
                        placeholder={
                          selectedValues.length === 0
                            ? (field.placeholder ?? 'Selecione ou digite...')
                            : ''
                        }
                        style={{
                          flex: 1,
                          minWidth: 80,
                          padding: theme.spacing.xs,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.fontSize.base,
                          color: theme.palette.text.primary,
                        }}
                      />
                    </div>
                    {showDropdown && (
                      <ul
                        id={`${baseId}-${field.name}-listbox`}
                        role="listbox"
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          margin: 0,
                          padding: 0,
                          listStyle: 'none',
                          maxHeight: AUTOCOMPLETE_DROPDOWN_MAX_HEIGHT,
                          overflowY: 'auto',
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.input.background}`,
                          borderRadius: theme.shape.borderRadius.md,
                          boxShadow:
                            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                          zIndex: 1,
                        }}
                      >
                        {filteredOptions.map((opt, idx) => (
                          <li
                            key={opt.value}
                            id={`${baseId}-${field.name}-opt-${opt.value}`}
                            role="option"
                            aria-selected={idx === highlightedIndex}
                            style={{
                              padding: theme.spacing.sm,
                              cursor: 'pointer',
                              fontFamily: theme.typography.fontFamily,
                              fontSize: theme.typography.fontSize.base,
                              color: theme.palette.text.primary,
                              backgroundColor:
                                idx === highlightedIndex
                                  ? theme.palette.input.background
                                  : 'transparent',
                            }}
                            onMouseEnter={() =>
                              setAutocompleteHighlightedIndex(idx)
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              const next = [...selectedValues, opt.value];
                              updateValue(
                                field.name,
                                next.join(AUTOCOMPLETE_MULTI_SEPARATOR)
                              );
                              setAutocompleteQuery(prev => ({
                                ...prev,
                                [field.name]: '',
                              }));
                              setOpenAutocomplete(null);
                              setAutocompleteHighlightedIndex(-1);
                            }}
                          >
                            {opt.label}
                          </li>
                        ))}
                        {field.loadOptions && fieldState.hasMore && (
                          <li
                            style={{
                              padding: theme.spacing.sm,
                              borderTop: `1px solid ${theme.palette.input.background}`,
                              textAlign: 'center',
                            }}
                          >
                            <button
                              type="button"
                              onClick={e => {
                                e.preventDefault();
                                if (fieldState.loading) return;
                                loadAutocompleteOptionsPage(field, {
                                  search: (autocompleteQuery[field.name] ?? '').trim(),
                                  page: fieldState.page + 1,
                                  append: true,
                                });
                              }}
                              disabled={fieldState.loading}
                              style={{
                                border: 'none',
                                background: 'none',
                                cursor: fieldState.loading ? 'default' : 'pointer',
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {fieldState.loading ? 'Carregando...' : 'Carregar mais'}
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                    {hasError && (
                      <div style={errorStyle}>Este campo é obrigatório</div>
                    )}
                  </div>
                );
              }

              const displayValue =
                options.find(o => o.value === value)?.label ?? value;
              const filteredOptionsSingle =
                field.loadOptions && value.trim()
                  ? options
                  : value.trim()
                    ? options.filter(
                        opt =>
                          opt.label
                            .toLowerCase()
                            .includes(value.trim().toLowerCase()) ||
                          opt.value
                            .toLowerCase()
                            .includes(value.trim().toLowerCase())
                      )
                    : options;
              const isOpen = openAutocomplete === field.name;
              const showDropdown = isOpen && filteredOptionsSingle.length > 0;
              const highlightedIndex =
                autocompleteHighlightedIndex < 0 ||
                autocompleteHighlightedIndex >= filteredOptionsSingle.length
                  ? -1
                  : autocompleteHighlightedIndex;

              return (
                <div
                  key={field.name}
                  ref={
                    openAutocomplete === field.name
                      ? autocompleteContainerRef
                      : undefined
                  }
                  style={{
                    position: 'relative',
                    marginBottom: theme.spacing.md,
                  }}
                >
                  <label style={labelStyle} htmlFor={`${baseId}-${field.name}`}>
                    {label}
                  </label>
                  <input
                    id={`${baseId}-${field.name}`}
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-autocomplete="list"
                    aria-controls={
                      showDropdown
                        ? `${baseId}-${field.name}-listbox`
                        : undefined
                    }
                    aria-activedescendant={
                      highlightedIndex >= 0 &&
                      filteredOptionsSingle[highlightedIndex]
                        ? `${baseId}-${field.name}-opt-${filteredOptionsSingle[highlightedIndex].value}`
                        : undefined
                    }
                    value={displayValue}
                    onChange={e => {
                      const nextValue = e.target.value;
                      updateValue(field.name, nextValue);
                      setOpenAutocomplete(field.name);
                      setAutocompleteHighlightedIndex(0);
                      if (field.loadOptions) {
                        loadAutocompleteOptionsPage(field, {
                          search: nextValue.trim(),
                          page: 1,
                          append: false,
                        });
                      }
                    }}
                    onFocus={() => {
                      setOpenAutocomplete(field.name);
                      setAutocompleteHighlightedIndex(0);
                      if (field.loadOptions) {
                        loadAutocompleteOptionsPage(field, {
                          search: String(value ?? '').trim(),
                          page: 1,
                          append: false,
                        });
                      }
                    }}
                    onKeyDown={e => {
                      if (!showDropdown) {
                        if (e.key === 'ArrowDown' || e.key === 'Enter') {
                          setOpenAutocomplete(field.name);
                          setAutocompleteHighlightedIndex(0);
                          if (field.loadOptions) {
                            loadAutocompleteOptionsPage(field, {
                              search: String(value ?? '').trim(),
                              page: 1,
                              append: false,
                            });
                          }
                        }
                        return;
                      }
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setAutocompleteHighlightedIndex(prev =>
                          prev < filteredOptionsSingle.length - 1 ? prev + 1 : 0
                        );
                        return;
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setAutocompleteHighlightedIndex(prev =>
                          prev <= 0
                            ? filteredOptionsSingle.length - 1
                            : prev - 1
                        );
                        return;
                      }
                      if (
                        e.key === 'Enter' &&
                        highlightedIndex >= 0 &&
                        filteredOptionsSingle[highlightedIndex]
                      ) {
                        e.preventDefault();
                        updateValue(
                          field.name,
                          filteredOptionsSingle[highlightedIndex].value
                        );
                        setOpenAutocomplete(null);
                        setAutocompleteHighlightedIndex(-1);
                      }
                      if (e.key === 'Escape') {
                        setOpenAutocomplete(null);
                        setAutocompleteHighlightedIndex(-1);
                      }
                    }}
                    placeholder={field.placeholder ?? 'Selecione ou digite...'}
                    style={{
                      ...inputBaseStyle,
                      border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                    }}
                  />
                  {showDropdown && (
                    <ul
                      id={`${baseId}-${field.name}-listbox`}
                      role="listbox"
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '100%',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        maxHeight: AUTOCOMPLETE_DROPDOWN_MAX_HEIGHT,
                        overflowY: 'auto',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.input.background}`,
                        borderRadius: theme.shape.borderRadius.md,
                        boxShadow:
                          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                        zIndex: 1,
                      }}
                    >
                      {filteredOptionsSingle.map((opt, idx) => (
                        <li
                          key={opt.value}
                          id={`${baseId}-${field.name}-opt-${opt.value}`}
                          role="option"
                          aria-selected={idx === highlightedIndex}
                          style={{
                            padding: theme.spacing.sm,
                            cursor: 'pointer',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.fontSize.base,
                            color: theme.palette.text.primary,
                            backgroundColor:
                              idx === highlightedIndex
                                ? theme.palette.input.background
                                : 'transparent',
                          }}
                          onMouseEnter={() =>
                            setAutocompleteHighlightedIndex(idx)
                          }
                          onMouseDown={e => {
                            e.preventDefault();
                            updateValue(field.name, opt.value);
                            setOpenAutocomplete(null);
                            setAutocompleteHighlightedIndex(-1);
                          }}
                        >
                          {opt.label}
                        </li>
                      ))}
                      {field.loadOptions && fieldState.hasMore && (
                        <li
                          style={{
                            padding: theme.spacing.sm,
                            borderTop: `1px solid ${theme.palette.input.background}`,
                            textAlign: 'center',
                          }}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              if (fieldState.loading) return;
                              loadAutocompleteOptionsPage(field, {
                                search: String(value ?? '').trim(),
                                page: fieldState.page + 1,
                                append: true,
                              });
                            }}
                            disabled={fieldState.loading}
                            style={{
                              border: 'none',
                              background: 'none',
                              cursor: fieldState.loading ? 'default' : 'pointer',
                              fontFamily: theme.typography.fontFamily,
                              fontSize: theme.typography.fontSize.sm,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {fieldState.loading ? 'Carregando...' : 'Carregar mais'}
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                  {hasError && (
                    <div style={errorStyle}>Este campo é obrigatório</div>
                  )}
                </div>
              );
            }

            if (field.type === 'checkboxgroup') {
              const options = field.options ?? [];
              const selectedValues = (value || '')
                .split(AUTOCOMPLETE_MULTI_SEPARATOR)
                .map(s => s.trim())
                .filter(Boolean);
              const toggleOption = (optValue: string) => {
                const next = selectedValues.includes(optValue)
                  ? selectedValues.filter(v => v !== optValue)
                  : [...selectedValues, optValue];
                updateValue(
                  field.name,
                  next.join(AUTOCOMPLETE_MULTI_SEPARATOR)
                );
              };
              const boxStyle: React.CSSProperties = {
                marginTop: theme.spacing.xs,
                padding: theme.spacing.md,
                border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.border.input}`,
                borderRadius: theme.shape.borderRadius.md,
                backgroundColor: theme.palette.input.background ?? theme.palette.background.paper,
                maxHeight: 200,
                overflowY: 'auto' as const,
              };
              const optionStyle: React.CSSProperties = {
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                padding: theme.spacing.sm,
                cursor: 'pointer',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.base,
                color: theme.palette.text.primary,
              };
              const optionLabelStyle: React.CSSProperties = {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.base,
                color: theme.palette.text.primary,
                lineHeight: theme.typography.lineHeight?.normal ?? 1.5,
              };
              const optionDescriptionStyle: React.CSSProperties = {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.sm,
                color: theme.palette.text.secondary ?? theme.palette.text.primary,
                lineHeight: theme.typography.lineHeight?.normal ?? 1.5,
                marginTop: theme.spacing.xs,
              };
              return (
                <div
                  key={field.name}
                  style={{ marginBottom: theme.spacing.md }}
                >
                  <label style={labelStyle}>
                    {label}
                  </label>
                  <div style={boxStyle} role="group" aria-label={field.label}>
                    {options.length === 0 ? (
                      <div style={{ ...optionStyle, cursor: 'default', opacity: 0.7 }}>
                        {field.placeholder ?? 'Nenhuma opção disponível'}
                      </div>
                    ) : (
                      options.map(opt => {
                        const checked = selectedValues.includes(opt.value);
                        const checkboxBoxStyle: React.CSSProperties = {
                          width: 18,
                          height: 18,
                          flexShrink: 0,
                          borderRadius: theme.shape.borderRadius.sm ?? 4,
                          border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.border.input}`,
                          backgroundColor: checked
                            ? theme.palette.primary.main
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.primary.contrastText ?? '#FFFFFF',
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1,
                        };
                        return (
                          <label
                            key={opt.value}
                            style={optionStyle}
                            htmlFor={`${baseId}-${field.name}-${opt.value}`}
                          >
                            <input
                              id={`${baseId}-${field.name}-${opt.value}`}
                              type="checkbox"
                              role="checkbox"
                              aria-checked={checked}
                              checked={checked}
                              onChange={() => toggleOption(opt.value)}
                              style={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                margin: -1,
                                padding: 0,
                                overflow: 'hidden',
                                clip: 'rect(0,0,0,0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                              }}
                            />
                            <span style={checkboxBoxStyle} aria-hidden>
                              {checked ? '✓' : ''}
                            </span>
                            <span style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={optionLabelStyle}>{opt.label}</span>
                              {opt.description ? (
                                <span style={optionDescriptionStyle}>
                                  {opt.description}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {hasError && (
                    <div style={errorStyle}>Este campo é obrigatório</div>
                  )}
                </div>
              );
            }

            if (field.type === 'select') {
              const options = field.options ?? [];
              const chevronColor = encodeURIComponent(
                theme.palette.text.secondary ?? theme.palette.text.primary
              );
              const chevronIcon = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='${chevronColor}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
              return (
                <div
                  key={field.name}
                  style={{ marginBottom: theme.spacing.md }}
                >
                  <label style={labelStyle} htmlFor={`${baseId}-${field.name}`}>
                    {label}
                  </label>
                  <select
                    id={`${baseId}-${field.name}`}
                    value={value}
                    onChange={e => updateValue(field.name, e.target.value)}
                    style={{
                      ...inputBaseStyle,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      paddingRight: theme.spacing.xl,
                      backgroundImage: chevronIcon,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: `right ${theme.spacing.md} center`,
                      backgroundSize: '12px 8px',
                      border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                    }}
                  >
                    <option value="" disabled>
                      {field.placeholder ?? 'Selecione uma opção'}
                    </option>
                    {options.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {hasError && (
                    <div style={errorStyle}>Este campo é obrigatório</div>
                  )}
                </div>
              );
            }

            if (field.type === 'date') {
              return (
                <div
                  key={field.name}
                  style={{ marginBottom: theme.spacing.md }}
                >
                  <label style={labelStyle} htmlFor={`${baseId}-${field.name}`}>
                    {label}
                  </label>
                  <input
                    id={`${baseId}-${field.name}`}
                    type="date"
                    value={value}
                    onChange={e => updateValue(field.name, e.target.value)}
                    style={{
                      ...inputBaseStyle,
                      border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                    }}
                  />
                  {hasError && (
                    <div style={errorStyle}>Este campo é obrigatório</div>
                  )}
                </div>
              );
            }

            if (field.type === 'switch') {
              const checked = value === 'true';
              return (
                <div
                  key={field.name}
                  style={{
                    marginBottom: theme.spacing.md,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing.md,
                  }}
                >
                  <label
                    style={{ ...labelStyle, marginBottom: 0 }}
                    htmlFor={`${baseId}-${field.name}`}
                  >
                    {label}
                  </label>
                  <div
                    style={{
                      position: 'relative',
                      width: 44,
                      height: 24,
                      flexShrink: 0,
                    }}
                  >
                    <input
                      id={`${baseId}-${field.name}`}
                      type="checkbox"
                      role="switch"
                      aria-checked={checked}
                      checked={checked}
                      onChange={e =>
                        updateValue(
                          field.name,
                          e.target.checked ? 'true' : 'false'
                        )
                      }
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        cursor: 'pointer',
                        width: '100%',
                        height: '100%',
                        margin: 0,
                      }}
                    />
                    <div
                      style={{
                        ...switchTrackStyle,
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        backgroundColor: checked
                          ? theme.palette.primary.main
                          : theme.palette.border.input,
                      }}
                    >
                      <div
                        style={{
                          ...switchThumbStyle,
                          transform: checked
                            ? 'translateX(20px)'
                            : 'translateX(0)',
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={field.name} style={{ marginBottom: theme.spacing.md }}>
                <label style={labelStyle} htmlFor={`${baseId}-${field.name}`}>
                  {label}
                </label>
                <input
                  id={`${baseId}-${field.name}`}
                  type={field.type ?? 'text'}
                  value={value}
                  onChange={e => updateValue(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  style={{
                    ...inputBaseStyle,
                    border: `${theme.shape.borderWidth.thin} solid ${hasError ? borderColor : theme.palette.input.background}`,
                  }}
                />
                {hasError && (
                  <div style={errorStyle}>
                    {isInvalidEmail
                      ? 'Informe um e-mail válido'
                      : 'Este campo é obrigatório'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={footerStyle}>
          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={handleAction(action)}
              data-e2e={
                dataE2ePrefix
                  ? `${dataE2ePrefix}-action-${index}`
                  : undefined
              }
              backgroundColor={
                action.outlined
                  ? theme.palette.background.paper
                  : action.variant === 'primary'
                    ? theme.palette.primary.main
                    : theme.palette.button?.filterBg ?? theme.palette.background.paper
              }
              color={
                action.outlined
                  ? theme.palette.text.primary
                  : action.variant === 'primary'
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.primary
              }
              style={
                action.outlined
                  ? {
                      border: `1px solid ${theme.palette.border.main}`,
                    }
                  : action.variant === 'secondary'
                    ? {
                        border: `1px solid ${theme.palette.button?.filterBg ?? theme.palette.background.paper}`,
                      }
                    : undefined
              }
            >
              {action.iconSrc ? (
                <img
                  src={action.iconSrc}
                  alt={action.iconAlt ?? ''}
                  width={action.iconSize ?? 18}
                  height={action.iconSize ?? 18}
                  style={{
                    marginRight: theme.spacing.xs,
                    verticalAlign: 'middle',
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                  aria-hidden={action.iconAlt ? undefined : true}
                />
              ) : null}
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
